import { NavLink } from 'react-router-dom';

import { Box, useTheme } from '@sbv-clients/ui-shared';

import { SideBarMenuItem } from '../sidebar/types';
import { isLinkActive, isSetupSection, useNavModel } from './use-nav-model';

// Page tabs under the top bar for sections with more than one page
// (Dashboards, Schedule, Reports). Setup / Configuration uses its own menu.
export const SectionTabs = ({ items }: { items: SideBarMenuItem[] }) => {
  const theme = useTheme();
  const { currentSection, pathname } = useNavModel(items);

  if (
    !currentSection ||
    currentSection.children.length < 2 ||
    isSetupSection(currentSection.item.label)
  ) {
    return null;
  }

  return (
    <Box
      component="nav"
      aria-label={`${currentSection.item.label} pages`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: 57,
        padding: { xs: '10px 12px', sm: '10px 24px' },
        background: theme.background.white,
        boxShadow: `inset 0 -1px 0 ${theme.custom?.shellSectionLine}`,
        flexShrink: 0,
      }}
    >
      <Box
        component="b"
        sx={{
          display: { xs: 'none', sm: 'block' },
          flexShrink: 0,
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: '0.72px',
          textTransform: 'uppercase',
          color: theme.custom?.shellSectionLabel,
        }}
      >
        {currentSection.item.label}
      </Box>
      <Box
        component="i"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: '1px',
          height: '22px',
          margin: '0 14px',
          flexShrink: 0,
          background: theme.custom?.shellSectionDivider,
        }}
      />
      <Box
        sx={{
          display: 'inline-flex',
          gap: '2px',
          padding: '3px',
          borderRadius: '10px',
          background: theme.custom?.shellSectionTrackBg,
          maxWidth: '100%',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {currentSection.children.map((child) => {
          const on = isLinkActive(child.link, pathname);
          return (
            <Box
              key={child.id ?? child.link}
              component={NavLink}
              to={child.link ?? '/'}
              aria-current={on ? 'page' : undefined}
              sx={{
                flexShrink: 0,
                padding: '6px 13px',
                borderRadius: '8px',
                fontSize: '13.5px',
                lineHeight: '19px',
                fontWeight: on ? 600 : 500,
                whiteSpace: 'nowrap',
                textDecoration: 'none',
                color: on
                  ? theme.custom?.brandNavy
                  : theme.custom?.shellSectionTabText,
                background: on ? theme.background.white : 'transparent',
                boxShadow: on ? theme.custom?.shellSectionTabShadow : 'none',
                '&:hover': { color: theme.custom?.brandNavy },
              }}
            >
              {child.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
